import { hasAnyRole, useAuth } from '@berky/auth'
import type { ModuleConfig } from '@berky/module-contract'
import { cn } from '@berky/ui'
import { NavLink } from 'react-router'

import { moduleAccess } from '../module-access'
import { modules } from '../modules'
import {
  useFavorites,
  useModuleNavigation,
  useNavigationCategories,
} from '../navigation/api'
import { findCategoryAssignment } from '../navigation/types'
import { FEEDBACK_ROLE } from '../feedback/feedback-constants'
import { PLATFORM_ADMIN_ROLE } from '../verwaltung/api'

/** Gemeinsame Sichtbarkeitsregeln für Seitenleiste und aufklappbares Menü. */
export function useNavigationItems() {
  const { user, isLoading, error } = useAuth()
  const navigation = useModuleNavigation()
  const categories = useNavigationCategories()
  const favorites = useFavorites()

  const allowedModules = modules.filter(
    (module) => !isLoading && !error && moduleAccess(user?.roles, module) === 'erlaubt',
  )
  const settings = new Map(
    (navigation.data ?? []).map((setting) => [setting.moduleId, setting]),
  )
  const activeCategories = (categories.data ?? [])
    .filter((category) => category.isActive)
    .toSorted((left, right) => left.sortOrder - right.sortOrder)
  const visibleModules = allowedModules.filter((module) => {
    const setting = settings.get(module.id)
    return (
      setting?.isVisible &&
      setting.categories.some((assignment) =>
        activeCategories.some((category) => category.id === assignment.categoryId),
      )
    )
  })
  const favoritesById = new Map(allowedModules.map((module) => [module.id, module]))

  return {
    isLoading: isLoading || navigation.isPending || categories.isPending,
    isError: Boolean(error) || navigation.isError || categories.isError,
    retry: () => {
      void navigation.refetch()
      void categories.refetch()
    },
    visibleModules,
    favoriteModules: (favorites.data?.moduleIds ?? [])
      .map((moduleId) => favoritesById.get(moduleId))
      .filter(
        (module): module is ModuleConfig =>
          module !== undefined && visibleModules.includes(module),
      ),
    groups: activeCategories.map((category) => ({
      category,
      modules: visibleModules
        .filter((module) => findCategoryAssignment(settings.get(module.id), category.id))
        .sort(
          (left, right) =>
            (findCategoryAssignment(settings.get(left.id), category.id)?.sortOrder ??
              Number.MAX_SAFE_INTEGER) -
              (findCategoryAssignment(settings.get(right.id), category.id)?.sortOrder ??
                Number.MAX_SAFE_INTEGER) || left.title.localeCompare(right.title, 'de'),
        ),
    })),
    canManageUsers:
      !isLoading && !error && hasAnyRole(user?.roles, [PLATFORM_ADMIN_ROLE]),
    canManageFeedback: !isLoading && !error && hasAnyRole(user?.roles, [FEEDBACK_ROLE]),
  }
}

/** Einheitliches Ziel in allen Darstellungen der Plattformnavigation. */
export function NavigationLink({
  to,
  label,
  children,
  trailing,
  end = false,
  onNavigate,
}: {
  to: string
  label: string
  children: React.ReactNode
  trailing?: React.ReactNode
  end?: boolean
  onNavigate?: () => void
}) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          'relative flex min-h-11 items-center gap-3 rounded-md px-3 text-sm transition-colors',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
          isActive
            ? 'bg-sidebar-accent font-medium text-sidebar-accent-foreground'
            : 'text-muted-foreground hover:bg-sidebar-accent/60 hover:text-foreground',
        )
      }
    >
      {({ isActive }) => (
        <>
          <span
            aria-hidden="true"
            className={cn(
              'absolute top-1.5 bottom-1.5 left-0 w-1 rounded-full bg-marker transition-opacity',
              isActive ? 'opacity-100' : 'opacity-0',
            )}
          />
          {children}
          <span className="min-w-0 flex-1 leading-snug">{label}</span>
          {trailing}
        </>
      )}
    </NavLink>
  )
}
